import React, { useEffect, useState, Dispatch, forwardRef, useImperativeHandle, Ref } from 'react';
import { useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { userFilesApi } from 'api';
import { Message } from 'primereact/message';
import { SessionFile } from 'types/SessionFile';
import { FileUpload } from 'primereact/fileupload';
import { urlValidator } from 'utils/urlValidator';
import { usePanel } from 'contexts/panelContext';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import { Dropdown } from 'components/_new/Dropdown';
import { InputText } from 'components/_new/InputText';
import { Field } from 'components/_new/Field';
import { Button } from 'components/_new/Button';
import { useGlobal } from 'contexts/globalContext';
import { DraftEditor } from '../../_common/DraftEditor/DraftEditor';
import ValidateCheckbox from '../../_common/forms/Checkbox';

interface UserFileFormObject {
	name: string;
	type: string;
	forAuthorOnly: boolean;
	file?: File | null;
	category: string;
	url: string;
	content: string;
	sendMailNotification?: boolean;
}

type UploadUserFilesProps = {
	userFile?: SessionFile;
	setUserFile: Dispatch<SessionFile | undefined>;
	onFinish?: (sessionFile: SessionFile) => void;
	onSave?: (sessionFile: SessionFile) => void;
	setHasChanges?: (hasChanges: boolean) => void;
	session?: any;
	onClose?: () => void;
	withAddNewButton?: boolean;
};

export type UploadUserFilesRef = {
	save: () => Promise<SessionFile>;
};

export const UploadUserFiles = forwardRef(
	(
		{
			onClose,
			userFile,
			setUserFile,
			onFinish,
			onSave,
			setHasChanges,
			session,
			withAddNewButton = false,
		}: UploadUserFilesProps,
		ref: Ref<UploadUserFilesRef>
	) => {
		const { t } = useTranslation();
		const { pairId } = useParams();

		const { toastRef } = useGlobal();
		const { currentProgramMembership, currentApplicationData, currentProgram } = usePanel();

		const defaultValues = {
			name: t('userPanel.userFiles.file.defaultName'),
			type: 'html',
			forAuthorOnly: userFile?.forAuthorOnly || false,
			file: undefined,
			url: '',
			content: '<p></p>\n',
			sendMailNotification: false,
		};
		const { control, handleSubmit, watch, reset, getValues } = useForm<UserFileFormObject>({
			defaultValues,
		});

		const [hasChangesLocal, setHasChangesLocal] = useState(false);

		const {
			mutate: uploadUserFileMutate,
			mutateAsync: uploadUserFileMutateAsync,
			isSuccess: uploadUserFileSuccess,
			isLoading: uploadUserFileLoading,
			reset: uploadUserFileReset,
		} = useMutation(
			(data: any) =>
				userFilesApi.uploadUserFile(
					currentProgramMembership.id,
					currentApplicationData.id,
					Number(pairId),
					data
				),
			{
				onSuccess: (data) => {
					if (setUserFile) {
						setUserFile(data);
					}
					if (onFinish) {
						onFinish(data);
					}
				},
			}
		);

		const [afterUpdate, setAfterUpdate] = useState(false);
		const {
			mutate: editUserFileMutate,
			mutateAsync: editUserFileMutateAsync,
			isSuccess: editUserFileSuccess,
			isLoading: editUserFileLoading,
			reset: editUserFileReset,
		} = useMutation(
			({ data }: { data: any; autosave?: boolean }) => userFilesApi.editUserFile(Number(userFile?.id), data),
			{
				onSuccess: (data, { autosave }) => {
					setHasChangesLocal(false);
					if (onSave) {
						onSave(data);
					}
					if (onFinish && !autosave) {
						onFinish(data);
						setAfterUpdate(true);
					}
				},
			}
		);

		useEffect(() => {
			if (userFile) {
				setHasChangesLocal(false);
				reset({
					name: userFile?.name || '',
					type: userFile?.type || 'html',
					forAuthorOnly: userFile?.forAuthorOnly || false,
					file: null,
					url: userFile?.url || '',
					content: userFile?.content || '<p></p>\n',
				});
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [userFile]);

		const fileTypeOptions = [
			{ label: t('userPanel.userFiles.file.typeOptions.pdf'), value: 'pdf' },
			{ label: t('userPanel.userFiles.file.typeOptions.video'), value: 'video' },
			// { label: t('userPanel.userFiles.file.typeOptions.link'), value: 'link' },
			{ label: t('userPanel.userFiles.file.typeOptions.html'), value: 'html' },
			{ label: t('userPanel.userFiles.file.typeOptions.file'), value: 'file' },
		];

		const name = watch('name');
		const type = watch('type');
		const url = watch('url');
		const content = watch('content');
		const file = watch('file');
		const forAuthorOnly = watch('forAuthorOnly');
		const sendMailNotification = watch('sendMailNotification');

		useEffect(() => {
			const nameChanged = userFile ? userFile.name !== name : name !== defaultValues.name;
			const urlChanged = userFile ? userFile.url !== url : url !== defaultValues.url;
			const typeChanged = userFile ? userFile.type !== type : type !== defaultValues.type;
			const contentChanged = userFile
				? userFile.content?.trim() !== content.trim()
				: content.trim() !== defaultValues.content.trim();

			const hasFile = Boolean(file);
			const hasChanges = nameChanged || urlChanged || typeChanged || contentChanged || hasFile;

			if (setHasChanges) {
				setHasChanges(hasChanges);
			}
			setHasChangesLocal(hasChanges);
		}, [
			name,
			setHasChanges,
			userFile,
			type,
			content,
			file,
			url,
			defaultValues.name,
			defaultValues.url,
			defaultValues.type,
			defaultValues.content,
		]);

		useEffect(() => {
			if (!userFile) return;

			const interval = setInterval(() => {
				if (hasChangesLocal) {
					const values = getValues();
					editUserFileMutateAsync({ data: values, autosave: true }).then(() => {
						toastRef?.current?.show({
							severity: 'success',
							life: 3000,
							summary: t('misc.success'),
							detail: t('userPanel.sessionsNew.reflection.autosaveMessage'),
						});
					});
				}
			}, 30000);

			// eslint-disable-next-line consistent-return
			return () => {
				clearInterval(interval);
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [hasChangesLocal, userFile]);

		const handleCloseClick = () => {
			if (onClose) {
				onClose();
			}
			setUserFile(undefined);
		};

		const resetModal = () => {
			setUserFile(undefined);
			uploadUserFileReset();
			editUserFileReset();
			reset(defaultValues);
		};
		const fileType = watch('type');
		const isSuccess = uploadUserFileSuccess || afterUpdate;
		const isLoading = uploadUserFileLoading || editUserFileLoading;
		const afterFormSubmit = isSuccess && type !== 'html';

		const handleSubmitForm = handleSubmit((values) => {
			if (userFile) {
				editUserFileMutate({ data: values, autosave: false });
			} else {
				uploadUserFileMutate({ sessionId: session?.id, ...values });
			}
		});

		useImperativeHandle(ref, () => ({
			save: () => {
				const values = getValues();
				if (userFile) {
					return editUserFileMutateAsync({ data: values, autosave: false });
				}
				return uploadUserFileMutateAsync({ sessionId: session?.id, ...values });
			},
		}));

		const [buttonDisabled, setButtonDisabled] = useState(false);
		useEffect(() => {
			if (isLoading) {
				setButtonDisabled(true);
				setTimeout(() => {
					setButtonDisabled(false);
				}, 2000);
			}
		}, [isLoading]);

		const handleAddNewClick = () => {
			resetModal();
		};

		useEffect(() => {
			const handleWindowKeydown = (event: KeyboardEvent) => {
				if (event.ctrlKey && String(event.key).toLowerCase() === 's') {
					event.preventDefault();
					handleSubmitForm(undefined);
				}
			};
			window.addEventListener('keydown', handleWindowKeydown);
			return () => {
				window.removeEventListener('keydown', handleWindowKeydown);
			};
		});

		const accept = (
			type === 'pdf'
				? ['application/pdf']
				: [
						'application/vnd.ms-excel',
						'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
						'application/vnd.ms-powerpoint',
						'application/vnd.openxmlformats-officedocument.presentationml.presentation',
						'application/msword',
						'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
						'application/vnd.oasis.opendocument.presentation',
						'application/vnd.oasis.opendocument.spreadsheet',
						'application/vnd.oasis.opendocument.text',
				  ]
		).join(',');

		const hasPrevFile = Boolean(userFile?.originalFilename);

		return (
			<form onSubmit={handleSubmitForm} className="flex flex-column gap-0">
				{!afterFormSubmit && (
					<>
						<Controller
							control={control}
							name="name"
							rules={{
								required: {
									value: true,
									message: t('misc.forms.fieldIsRequired'),
								},
							}}
							render={({ field, fieldState: { error } }) => (
								<Field
									label={t('userPanel.userFiles.file.name')}
									htmlFor={field.name}
									errorMessage={error?.message}
								>
									<InputText id={field.name} {...field} />
								</Field>
							)}
						/>

						<Controller
							rules={{
								required: {
									value: true,
									message: t('userPanel.userFiles.file.typeRequired'),
								},
							}}
							name="type"
							control={control}
							render={({ field, fieldState: { error } }) => (
								<Field
									label={t('userPanel.userFiles.file.type')}
									htmlFor={field.name}
									errorMessage={error?.message}
								>
									<Dropdown
										inputId={field.name}
										{...field}
										options={fileTypeOptions}
										placeholder={t('userPanel.userFiles.file.type')}
										className="w-full"
									/>
								</Field>
							)}
						/>

						{['pdf', 'file'].includes(fileType) && (
							<Controller
								name="file"
								control={control}
								rules={{
									required: {
										value: hasPrevFile ? false : ['pdf', 'file'].includes(fileType),
										message: t('userPanel.userFiles.file.fileRequired'),
									},
								}}
								render={({ field: { value, onChange, ...field }, fieldState: { error } }) => (
									<Field
										label={t(`userPanel.userFiles.file.typeOptions.${fileType}`)}
										htmlFor={field.name}
										errorMessage={error?.message}
										contentClassName="px-2"
									>
										<div className="w-full">
											{!value && (
												<div className="w-full flex flex-row gap-4 align-items-center justify-content-between">
													<div className="flex-1">
														{userFile ? (
															<>
																{t('userPanel.userFiles.file.originalFilename')}:{' '}
																{userFile?.originalFilename || '-'}
															</>
														) : (
															<span className="text-muted">
																{t('userPanel.userFiles.file.fileRequired')}
															</span>
														)}
													</div>
													<FileUpload
														id={field.name}
														mode="basic"
														accept={accept}
														customUpload
														onSelect={({ originalEvent: event }) => {
															if (((event?.target as any)?.files?.length || 0) > 0) {
																onChange((event.target as any).files[0]);
															}
														}}
														chooseLabel={t('misc.choose')}
													/>
												</div>
											)}
											{value && (
												<div className="w-full flex gap-4 align-items-center justify-content-between">
													<span className="flex-1">{value.name}</span>
													<Button
														label={t('actions.remove')}
														onClick={() => {
															onChange(undefined);
														}}
														icon="times"
														variant="danger"
														variantSize="sm"
														className="p-button-danger p-button-xs"
													/>
												</div>
											)}
										</div>
									</Field>
								)}
							/>
						)}

						{fileType === 'video' && (
							<Controller
								control={control}
								name="url"
								rules={{
									required: {
										value: fileType === 'video',
										message: t('misc.forms.fieldIsRequired'),
									},
									validate: (value) => {
										if (fileType === 'video' && !urlValidator(value as string)) {
											return t('userPanel.userFiles.file.invalidUrl');
										}
										return true;
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<Field
										label={t('userPanel.userFiles.file.urlVideo')}
										htmlFor={field.name}
										errorMessage={error?.message}
									>
										<InputText id={field.name} {...field} />
									</Field>
								)}
							/>
						)}

						{fileType === 'link' && (
							<Controller
								control={control}
								name="url"
								rules={{
									required: {
										value: fileType === 'link',
										message: t('misc.forms.fieldIsRequired'),
									},
									validate: (value) => {
										if (fileType === 'link' && !urlValidator(value as string)) {
											return t('userPanel.userFiles.file.invalidUrl');
										}
										return true;
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<Field
										label={t('userPanel.userFiles.file.urlLink')}
										htmlFor={field.name}
										errorMessage={error?.message}
									>
										<InputText id={field.name} {...field} />
									</Field>
								)}
							/>
						)}

						<Tooltip target="#forAuthorOnly" position="bottom" disabled={!sendMailNotification}>
							{t('userPanel.userFiles.file.forMeOnlyHint')}
						</Tooltip>
						<Controller
							name="forAuthorOnly"
							control={control}
							render={({ field, fieldState: { error } }) => (
								<Field label="" htmlFor={field.name} errorMessage={error?.message} id="forAuthorOnly">
									<Dropdown
										{...field}
										value={sendMailNotification ? false : field.value}
										options={[
											{
												label: t('userPanel.userFiles.file.forMeOnly'),
												value: true,
											},
										]}
										withEmpty
										emptyLabel={t('userPanel.userFiles.file.forMeAndPair')}
										emptyValue={false}
										className="w-full"
										disabled={sendMailNotification}
									/>
								</Field>
							)}
						/>

						{['link', 'video', 'html', 'pdf', 'file'].includes(fileType) && (
							<Controller
								rules={{
									required: {
										value: ['html'].includes(fileType),
										message: t('userPanel.userFiles.file.contentRequired'),
									},
								}}
								name="content"
								control={control}
								render={({ field: { onChange }, fieldState: { error } }) => (
									<Field
										label={
											['link', 'video', 'pdf'].includes(fileType)
												? t('userPanel.userFiles.file.optionalDescription')
												: t('userPanel.userFiles.file.content')
										}
										errorMessage={error?.message}
									>
										<DraftEditor
											defaultValue={userFile?.content || ''}
											onChange={(value) => {
												onChange(value);
											}}
											uploadRequestFunction={(file) =>
												userFilesApi.uploadImage(
													file,
													Number(currentProgram.id),
													Number(pairId)
												)
											}
										/>
									</Field>
								)}
							/>
						)}

						{!forAuthorOnly && (
							<div className="flex flex-column gap-2 justify-items-start align-items-start">
								{!userFile && (
									<ValidateCheckbox
										id="sendMailNotification"
										control={control}
										name="sendMailNotification"
										text={t('userPanel.userFiles.file.sendMailNotification')}
										disabled={forAuthorOnly}
									/>
								)}
							</div>
						)}
					</>
				)}
				{editUserFileSuccess ? (
					<div>
						<Message
							severity="success"
							className="flex w-full mt-3 p-3"
							text={t('userPanel.userFiles.editedSuccessfully')}
						/>
					</div>
				) : (
					uploadUserFileSuccess && (
						<div>
							<Message
								severity="success"
								className="flex w-full mt-3 p-3"
								text={t('userPanel.userFiles.addedSuccessfully')}
							/>
						</div>
					)
				)}

				<div className="flex flex-row justify-content-between mt-2">
					{afterFormSubmit ? (
						<>
							{withAddNewButton ? (
								<Button
									label={t('userPanel.userFiles.addNewFileButton')}
									onClick={handleAddNewClick}
									variant="primary-outlined"
									className="w-5"
								/>
							) : (
								<span />
							)}
							<Button
								label={t('actions.close')}
								onClick={handleCloseClick}
								variant="primary-outlined"
								className="w-5"
							/>
						</>
					) : (
						<>
							<div />
							<Button
								submit
								className="w-5"
								loading={isLoading}
								label={userFile ? t('actions.save') : t('actions.add')}
								disabled={isLoading || buttonDisabled}
							/>
						</>
					)}
				</div>
			</form>
		);
	}
);
